import { IProjectRole } from "@kuika/kuika-cl-model";
import React from "react";
import KuikaButton from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaDropdownMenu from "../../../../ui-kit/kuika-dropdown-menu/kuika-dropdown-menu";
import KuikaInput from "../../../../ui-kit/kuika-input/kuika-input";
import KuikaMenuItem from "../../../../ui-kit/kuika-menu-item/kuika-menu-item";
import KuikaSelectbox from "../../../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSvg from "../../../../ui-kit/kuika-svg/kuika-svg";
import KuikaSmallText from "../../../../ui-kit/typography/SmallText/SmallText";
import KuikaText from "../../../../ui-kit/typography/Text/Text";
import { EditingState } from "./role-management-modal";

interface RoleRowProps {
  role: IProjectRole;
  itemKey?: string;
  level?: number;
  editing: EditingState | null;
  screens: any[];
  showDragHandle?: boolean;
  onStartEdit: (role: IProjectRole) => void;
  onConfirmEdit: (role: IProjectRole) => void;
  onCancelEdit: () => void;
  onUpdateField: (field: keyof EditingState, value: string) => void;
  onDeleteRole: (role: IProjectRole) => void;
}

export const RoleRow: React.FC<RoleRowProps> = ({
  role,
  itemKey,
  level = 0,
  editing,
  screens,
  showDragHandle = true,
  onStartEdit,
  onConfirmEdit,
  onCancelEdit,
  onUpdateField,
  onDeleteRole
}) => {
  const isEditing = editing?.type === "role" && editing.id === role.id;
  const screenOptions = screens.map((s) => ({ value: s.id, label: s.name }));
  const currentScreen = screens.find((s) => s.id === role.startingScreenId);

  const handleScreenChange = (value: string) => {
    onUpdateField("startingScreenId", value);
  };

  const roleMenuOptions = [
    {
      key: "edit",
      label: "Edit",
      onClick: () => onStartEdit(role)
    },
    {
      key: "delete",
      label: "Delete",
      onClick: () => onDeleteRole(role),
      danger: true
    }
  ];

  return (
    <KuikaMenuItem
      key={itemKey ?? `r-${role.id}`}
      className={isEditing ? "role-row--editing" : ""}
      leftIcon={
        <div
          className="role-management-role-item-content"
          style={{ paddingLeft: isEditing ? "0px" : `${level * 24}px` }}
        >
          <KuikaSvg name="key" width={16} height={16} />
        </div>
      }
      title={
        <div className="role-management-role-item-content">
          {isEditing ? (
            <div className="role-management-role-item__editing-content">
              {/* Renaming a role would orphan its KRole row whenever the designer id and the row's id
                  have drifted apart, so only the starting screen is editable here. */}
              <div className="editing-content__input">
                <KuikaInput value={editing.name} disabled />
              </div>
              <div className="editing-content__selectbox">
                <KuikaSelectbox
                  placeholder="Choose starting screen"
                  options={screenOptions}
                  value={editing.startingScreenId}
                  onChange={handleScreenChange}
                  className="role-management-starting-screen-select"
                  overlayClassName="role-management-starting-screen-select__overlay"
                  searchable
                  allowClear
                />
              </div>
            </div>
          ) : (
            <div className="kuika-flex-center--gap-s">
              <KuikaText>{role.name}</KuikaText>
              <KuikaSmallText>{currentScreen?.name || ""}</KuikaSmallText>
            </div>
          )}
        </div>
      }
      rightDropdown={
        isEditing ? (
          <>
            <KuikaButton isIcon type="tertiary" aria-label="Confirm" onClick={() => onConfirmEdit(role)}>
              <KuikaSvg name="check" width={16} height={16} />
            </KuikaButton>
            <KuikaButton isIcon type="tertiary" color="neutral" aria-label="Cancel" onClick={onCancelEdit}>
              <KuikaSvg name="x" width={16} height={16} />
            </KuikaButton>
          </>
        ) : (
          <KuikaDropdownMenu options={roleMenuOptions}>
            <KuikaButton isIcon type="tertiary" color="neutral" size="sm" aria-label="Role menu">
              <KuikaSvg name="dots" type="common" width={16} height={16} />
            </KuikaButton>
          </KuikaDropdownMenu>
        )
      }
      showDragHandle={showDragHandle}
    />
  );
};
